"use client";

import {
  type AsyncClickHandler,
  useAsyncClickHandler,
} from "@faire/react/hooks/useAsyncClickHandler";
import { SVGIconProps } from "@faire/slate-icons/types";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { ButtonBase } from "@faire/slate/private/button/ButtonBase";
import { ButtonIcon } from "@faire/slate/private/button/ButtonIcon";
import {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
  IButtonWebProps,
} from "@faire/slate/private/button/types";
import { usePolimorphicAttributes } from "@faire/slate/private/button/usePolimorphicAttributes";
import type { IText } from "@faire/slate/private/shared/types";
import { isNotUndefined } from "@faire/typescript";
import React from "react";

interface IIconProps {
  /**
   * The icon component to render
   */
  Component: React.FC<SVGIconProps>;

  /**
   * The position of the icon.
   *
   * `"start"`, `"end"`
   *
   * On LTR, start is left and end is right.
   * On RTL, start is right and end is left.
   *
   * @default "start"
   */
  position?: ButtonIconPosition;
}

type IProps = {
  /**
   * Button variant
   *
   * @default "primary"
   */
  variant?: ButtonVariant;
  /**
   * Button size (doesn't affect plain buttons)
   *
   * xSmall (height 32px), small (height 40px), medium (height 48px)
   *
   * @default "medium"
   */
  size?: ButtonSize;
  /**
   * Whether the button has the destructive variant.
   *
   * @default false
   */
  destructive?: boolean;
  /**
   * Shows a loading spinner.
   *
   * @default false
   */
  loading?: boolean;
  /**
   * The label to display while the button is loading.
   */
  loadingLabel?: string;
  /**
   * Whether the button is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The {@link IIconProps} to display.
   *
   * ```ts
   * interface IIconProps {
   *   // The icon component to render
   *   Component: React.FC<SVGIconProps>;
   *   // The position of the icon
   *   // "start" (default) | "end"
   *   position?: ButtonIconPosition;
   * }
   * ```
   */
  iconProps?: IIconProps;
  /**
   * The button label.
   */
  children: IText | string[] | Array<number | string>;

  /**
   * **web**
   * Whether the button should take the full width of the container
   * (affects primary and secondary variants only).
   *
   * @default false
   */
  fullWidth?: boolean;

  /**
   * The maximum number of lines the label may occupy before it is truncated.
   *
   * Plain buttons wrap freely unless this is set; every other variant
   * truncates at 2 lines by default.
   */
  lineLimit?: number;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IButtonWebProps;

/**
 * Buttons help our users take action or make a choice across a hierarchy of emphasis.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Button-2242efb5c25a807d9f14d4797a5be3f4 | Slate/Button}
 */
export const Button: React.FC<IProps> = ({
  children,
  destructive = false,
  disabled,
  fullWidth = false,
  iconProps,
  lineLimit,
  loading,
  loadingLabel,
  onClick,
  size = "medium",
  variant = "primary",
  ...props
}) => {
  const { handleClick, isPending, isPendingDelayed } = useAsyncClickHandler(
    disabled,
    loading,
    onClick as AsyncClickHandler | undefined
  );
  const { as, linkProps } = usePolimorphicAttributes(props);

  const buttonCls = buttonStyle.cls({
    variant,
    destructive,
    fullWidth,
    isLoading: isPending,
    size,
    className: componentCls(),
  });

  const iconPosition = iconProps?.position ?? "start";
  const icon = iconProps?.Component ? (
    <ButtonIcon
      Icon={iconProps.Component}
      className={iconStyle.cls({
        position: iconPosition,
        className: "fs-button__icon",
      })}
      titleAccess=""
    />
  ) : null;

  const hasIcon = icon !== null;

  const { linkTo: _, ...rest } = props;

  return (
    <ButtonBase
      {...rest}
      {...linkProps}
      aria-disabled={isPending || disabled}
      as={as}
      className={buttonCls}
      disabled={disabled}
      loading={isPending ? isPendingDelayed : undefined}
      loadingLabel={loadingLabel}
      onClick={handleClick}
      data-component-name="Slate/Button"
    >
      {iconPosition === "start" ? icon : null}
      <ButtonLabel variant={variant} hasIcon={hasIcon} lineLimit={lineLimit}>
        {children}
      </ButtonLabel>
      {iconPosition === "end" ? icon : null}
    </ButtonBase>
  );
};

const ButtonLabel: React.FC<
  React.PropsWithChildren<{
    variant: ButtonVariant;
    hasIcon: boolean;
    lineLimit: number | undefined;
  }>
> = ({ children, variant, hasIcon, lineLimit }) => {
  const isPlain = variant === "plain";
  const resolvedLineLimit =
    lineLimit ?? (isPlain ? undefined : DEFAULT_LINE_LIMIT);
  const isClamped = isNotUndefined(resolvedLineLimit);

  return (
    <span
      className={labelStyle.cls({ isPlain, hasIcon, isClamped })}
      style={labelStyle.vars({ lineLimit: resolvedLineLimit })}
    >
      {children}
    </span>
  );
};

const buttonStyle = tsv({
  base: "fs-button",
  variants: {
    variant: {
      primary: "fs-button--primary",
      secondary: "fs-button--secondary",
      tertiary: "fs-button--tertiary",
      plain: "fs-button--plain",
    },
    fullWidth: { true: "w-full" },
    isLoading: { true: "cursor-wait" },
    destructive: { true: "fs-button--destructive" },
    size: {
      xSmall: "fs-button--x-small",
      small: "fs-button--small",
      medium: "fs-button--medium",
    },
  },
});

const labelStyle = tsv({
  vars: {
    lineLimit: "fs-btn-line-limit",
  },
  base: "",
  variants: {
    isPlain: {
      true: "items-start",
      false: "text-center",
    },
    isClamped: {
      true: "fs-button__label--clamped",
      false: "",
    },
    hasIcon: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      isPlain: true,
      isClamped: false,
      class: "inline",
    },
    {
      isPlain: false,
      hasIcon: true,
      class: "text-start",
    },
  ],
});

const DEFAULT_LINE_LIMIT = 2;

const iconStyle = tsv({
  variants: {
    position: {
      start: "mr-(--slate-spacing-element-small)",
      end: "ml-(--slate-spacing-element-small)",
    },
  },
});
